import Icon from '@material-ui/core/Icon'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import { MUIDataTableColumnDef } from 'mui-datatables'
import React, { Fragment, useState } from 'react'
import { EnumUser } from '../../../../../graphql/typings/global_types'
import DataTable from '../../../../components/DataTable'
import { ConfirmDialog } from '../../../../components/Dialog'
import Panel from '../../../../components/Panel'
import { DisasterCategory } from '../../../../helpers/data/models/disaster/DisasterCategory'
import { useErrorService } from '../../../../helpers/errors/ErrorService'
import { useAuthUser } from '../../../../hooks/useAuth'
import { useCommonDataLoadedExisting } from '../../../../hooks/useCommonData'
import { useDeleteDisasterCategory } from '../../../../hooks/useMutations'
import useSnackBar from '../../../../hooks/useSnackBar'
import DisasterCategoryDialog from './DisasterCategoryDialog'

type DialogState =
    | {
          open: true
          disasterCategory: DisasterCategory
      }
    | { open: false }

type FormattedCategory = {
    id: string
    title: string
    description: string
}

const AEADisasterCategoriesList = () => {
    const { disasterCategories } = useCommonDataLoadedExisting()
    const { errorAlert } = useErrorService()
    const auth = useAuthUser()
    const isAdmin = auth.user.userType === EnumUser.admin
    const { openSnack } = useSnackBar()
    const deleteDisasterCategoryMutation = useDeleteDisasterCategory()

    const [openAddDialog, setOpenAddDialog] = useState<boolean>(false)
    const [openEditDialog, setOpenEditDialog] = useState<DialogState>({ open: false })
    const [openDeleteDialog, setOpenDeleteDialog] = useState<DialogState>({
        open: false,
    })
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false)

    const columns: MUIDataTableColumnDef[] = [
        {
            name: 'title',
            label: 'Nom',
        },
        {
            name: 'description',
            label: 'Description',
        },
    ]

    const addFeatureData = {
        addHandler: () => {
            setOpenAddDialog(true)
        },
        tooltip: `Ajout d'une catégorie de sinistre`,
    }

    const onCreated = (disasterCategory: DisasterCategory) => {
        openSnack({
            type: 'success',
            message: `Catégorie ${disasterCategory.title} créée avec succès !`,
        })
    }

    const onEdited = (disasterCategory: DisasterCategory) => {
        openSnack({
            message: 'Catégorie modifiée avec succès !',
            type: 'success',
        })
    }

    const onEditClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        disasterCategory: DisasterCategory
    ) => {
        event.stopPropagation()
        setOpenEditDialog({ open: true, disasterCategory })
    }

    const onDeleteClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        disasterCategory: DisasterCategory
    ) => {
        event.stopPropagation()
        setOpenDeleteDialog({ open: true, disasterCategory })
    }

    const onDeleteConfirmed = async () => {
        if (!openDeleteDialog.open) return

        setDeleteLoading(true)

        try {
            const disasterCategory = openDeleteDialog.disasterCategory

            const response = await deleteDisasterCategoryMutation.run({
                id: disasterCategory.id,
            })

            if (response.errors.length > 0) {
                throw 'Une erreur est survenue pendant la suppression de la catégorie.'
            } else {
                openSnack({
                    message: 'Catégorie supprimée avec succès !',
                    type: 'success',
                })
            }
        } catch (error) {
            errorAlert(error)
        } finally {
            setDeleteLoading(false)
            setOpenDeleteDialog({ open: false })
        }
    }

    const actionsColumn = (dataIndex: number, rowIndex: number) => {
        if (!isAdmin) return null

        const disasterCategory = disasterCategories[dataIndex]

        return (
            <div className="flex items-center">
                <div className="flex-grow"></div>
                <Tooltip title="Modifier la catégorie">
                    <IconButton onClick={(event) => onEditClick(event, disasterCategory)}>
                        <Icon>edit</Icon>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Supprimer la catégorie">
                    <IconButton onClick={(event) => onDeleteClick(event, disasterCategory)}>
                        <Icon>delete</Icon>
                    </IconButton>
                </Tooltip>
            </div>
        )
    }

    const formattedCategories: FormattedCategory[] = disasterCategories.map((category) => {
        return {
            ...category,
            description: category.description || '-',
        }
    })

    return (
        <Fragment>
            <Panel>
                <DataTable<FormattedCategory>
                    title={'Catégories de sinistres'}
                    data={formattedCategories}
                    columns={columns}
                    showSearch={true}
                    addFeature={addFeatureData}
                    actionsColumn={actionsColumn}
                    options={{
                        sortOrder: {
                            name: 'title',
                            direction: 'asc',
                        },
                    }}
                />
            </Panel>
            <DisasterCategoryDialog
                onDone={onCreated}
                open={openAddDialog}
                closeDialog={() => setOpenAddDialog(false)}
            />
            {openEditDialog.open && (
                <DisasterCategoryDialog
                    onDone={onEdited}
                    open={openEditDialog.open}
                    closeDialog={() => setOpenEditDialog({ open: false })}
                    disasterCategory={openEditDialog.disasterCategory}
                />
            )}
            {openDeleteDialog.open && (
                <ConfirmDialog
                    open={openDeleteDialog.open}
                    onCancelled={() => setOpenDeleteDialog({ open: false })}
                    onConfirmed={onDeleteConfirmed}
                    title="Suppression d'une catégorie de sinistre"
                    text={`Voulez-vous vraiment supprimer la catégorie "${openDeleteDialog.disasterCategory.title}" ? Tous les sinistres associés à cette catégorie seront supprimés !`}
                    loading={deleteLoading}
                />
            )}
        </Fragment>
    )
}

export default AEADisasterCategoriesList
